import * as React from "react";
import {
  createFileRoute,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { supabase } from "@/lib/supabase/supabaseClient";
import { store } from "@/lib/states/store";
import LoadingSpinner from "@/components/LoadingSpinner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { resolvePostAuthDestination } from "@/lib/auth/postAuthDestination";

const initSessionStore = async (force = true) => {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error || !user) {
    throw new Error(error?.message ?? "No authenticated user found");
  }

  await store.getState().session.initWithUserAsync(user, { force });
};

export const Route = createFileRoute("/auth/callback")({
  component: AuthCallback,
  beforeLoad: async ({ context }) => {
    if (!context.session) return;

    const sessionState = store.getState().session;
    if (!sessionState.initialised || sessionState.needsRefreshAfterRehydrate) {
      await sessionState.initWithUserAsync(context.session.user, {
        force: true,
      });
    }

    const destination = await resolvePostAuthDestination(context.session.user.id);
    throw redirect({
      to: destination,
      replace: true,
    });
  },
});

function AuthCallback() {
  const navigate = useNavigate();

  React.useEffect(() => {
    const run = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        navigate({ to: "/login", replace: true });
        return;
      }

      await initSessionStore();
      const destination = await resolvePostAuthDestination(session.user.id);
      navigate({ to: destination, replace: true });
    };

    void run();
  }, [navigate]);

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <a href="#" className="flex items-center gap-2 self-center font-medium">
          <img src="/lockupBlack.svg" alt="Trainrr" className="h-6" />
        </a>
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-xl">Signing you in</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <LoadingSpinner />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
